import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Route,
  Globe,
  Shield,
  X,
  Download,
  AlertTriangle,
} from "lucide-react";
import Editor from "@monaco-editor/react";
import type { ServiceRoute } from "@/types/admin";
import adminService from "@/services/admin-service";
import yaml from "js-yaml";
import { Card, CardBody } from "@/components/ui/Card";
import { useAuthStore } from "@/stores/auth-store";

export default function RoutesPage() {
  const { isOrgAdmin } = useAuthStore();
  const canManage = isOrgAdmin();

  const [routes, setRoutes] = useState<ServiceRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showYamlModal, setShowYamlModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState<ServiceRoute | null>(null);
  const [yamlContent, setYamlContent] = useState("");

  useEffect(() => {
    loadRoutes();
  }, []);

  const loadRoutes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.listRoutes();
      // Handle both formats: direct array or { routes: [...] }
      if (Array.isArray(data)) {
        setRoutes(data);
      } else if (
        data &&
        typeof data === "object" &&
        "routes" in data &&
        Array.isArray((data as any).routes)
      ) {
        setRoutes((data as any).routes);
      } else {
        setRoutes([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load routes");
      console.error(err);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const routeToYaml = (route: ServiceRoute): string => {
    const serviceConfig: any = {
      url: route.url,
      description: route.description || "",
      quota_limit: route.quota_limit,
      quota_unit: route.quota_unit,
      health_endpoint: route.health_endpoint,
      docs_endpoint: route.docs_endpoint,
      redoc_endpoint: route.redoc_endpoint,
      openapi_endpoint: route.openapi_endpoint,
      api_path: route.api_path,
      path_transformation: route.path_transformation,
    };

    // Only add service_headers if it has content
    if (
      route.service_headers &&
      Object.keys(route.service_headers).length > 0
    ) {
      serviceConfig.service_headers = route.service_headers;
    }

    const routeObj = {
      [route.service_name]: serviceConfig,
    };
    return yaml.dump(routeObj, { indent: 2 });
  };

  const handleCardClick = (route: ServiceRoute) => {
    setEditingRoute(route);
    setYamlContent(routeToYaml(route));
    setShowYamlModal(true);
  };

  const handleCreate = () => {
    setEditingRoute(null);
    const template = {
      "service-name": {
        url: "https://service.example.com",
        description: "Service description",
        quota_limit: 500,
        quota_unit: "requests/min",
        health_endpoint: "/api/v1/health",
        docs_endpoint: "/api/v1/docs",
        redoc_endpoint: "/api/v1/redoc",
        openapi_endpoint: "/api/v1/openapi.json",
        api_path: "/api/v1",
        path_transformation: "strip_prefix",
        service_headers: {
          "X-Custom-Header": "value",
          Authorization: "Bearer token",
        },
      },
    };
    setYamlContent(yaml.dump(template, { indent: 2 }));
    setShowYamlModal(true);
  };

  const handleSaveYaml = async () => {
    try {
      setSaving(true);
      setError(null);
      const parsed: any = yaml.load(yamlContent);
      const serviceName = Object.keys(parsed)[0];
      const config = parsed[serviceName];

      if (editingRoute) {
        // Update existing route
        await adminService.updateRoute(editingRoute.id, {
          url: config.url,
          description: config.description,
          quota_limit: config.quota_limit,
          quota_unit: config.quota_unit,
          health_endpoint: config.health_endpoint,
          docs_endpoint: config.docs_endpoint,
          redoc_endpoint: config.redoc_endpoint,
          openapi_endpoint: config.openapi_endpoint,
          api_path: config.api_path,
          path_transformation: config.path_transformation,
          service_headers: config.service_headers,
        });
      } else {
        // Create new route
        await adminService.createRoute({
          service_name: serviceName,
          url: config.url,
          description: config.description,
          quota_limit: config.quota_limit,
          quota_unit: config.quota_unit,
          health_endpoint: config.health_endpoint,
          docs_endpoint: config.docs_endpoint,
          redoc_endpoint: config.redoc_endpoint,
          openapi_endpoint: config.openapi_endpoint,
          api_path: config.api_path,
          path_transformation: config.path_transformation,
          service_headers: config.service_headers,
          enabled: true,
        });
      }
      setShowYamlModal(false);
      loadRoutes();
    } catch (err: any) {
      setError(err.message || "Failed to save route");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (route: ServiceRoute, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const confirmed = window.confirm(
      `Are you sure you want to delete the route for "${route.service_name}"?\n\nThis action cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      await adminService.deleteRoute(route.id);
      loadRoutes();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete route");
    }
  };

  const handleToggle = async (route: ServiceRoute, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await adminService.toggleRoute(route.id, !route.enabled);
      loadRoutes();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to toggle route");
    }
  };

  const handleDownload = () => {
    const allRoutes: any = {};
    routes.forEach((route) => {
      allRoutes[route.service_name] = {
        url: route.url,
        description: route.description,
        quota_limit: route.quota_limit,
        quota_unit: route.quota_unit,
        health_endpoint: route.health_endpoint,
        docs_endpoint: route.docs_endpoint,
        redoc_endpoint: route.redoc_endpoint,
        openapi_endpoint: route.openapi_endpoint,
        api_path: route.api_path,
        path_transformation: route.path_transformation,
      };
    });
    const yamlStr = yaml.dump({ routes: allRoutes }, { indent: 2 });
    const blob = new Blob([yamlStr], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "routes.yaml";
    a.click();
  };

  const filteredRoutes = Array.isArray(routes)
    ? routes.filter(
        (route) =>
          route.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          route.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
          route.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : [];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-4xl font-bold">Service Routes</h1>
            <p className="text-lg text-teal-100">
              Manage service routing configuration with YAML
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm sm:block">
              <p className="text-sm text-teal-100">Total Routes</p>
              <p className="text-2xl font-bold">{filteredRoutes.length}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <Download className="h-4 w-4" />
                Export YAML
              </button>
              {canManage && (
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                >
                  <Plus className="h-4 w-4" />
                  Create Route
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardBody>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search routes by service name, URL, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-3 pl-10 pr-4 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white dark:placeholder-gray-500"
            />
          </div>
        </CardBody>
      </Card>

      {/* Route Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredRoutes.map((route) => (
          <div
            key={route.id}
            onClick={() => handleCardClick(route)}
            className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200/60 bg-gradient-to-br from-white via-white to-gray-50/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] hover:border-blue-500/30 hover:shadow-xl dark:border-gray-700/60 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900/50 dark:shadow-gray-900/25 dark:hover:border-blue-400/30"
          >
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            {/* Content */}
            <div className="relative p-6">
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                    <Route className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 transition-colors group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                      {route.service_name}
                    </h3>
                    <div className="mt-1 flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${route.enabled ? "bg-green-500 shadow-lg shadow-green-500/30" : "bg-gray-400 dark:bg-gray-500"}`}
                      />
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {route.enabled ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => handleToggle(route, e)}
                  className={`relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                    route.enabled
                      ? "bg-green-100 text-green-700 shadow-sm hover:bg-green-200 hover:shadow-md dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/40"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                  }`}
                >
                  {route.enabled && <Shield className="h-3 w-3" />}
                  {route.enabled ? "Enabled" : "Disabled"}
                </button>
              </div>

              {/* Description */}
              {route.description && (
                <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  {route.description}
                </p>
              )}

              {/* URL */}
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-gray-200/60 bg-gray-50/80 p-3 dark:border-gray-600/60 dark:bg-gray-700/50">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700">
                  <Globe className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                    Endpoint
                  </div>
                  <div className="truncate font-mono text-sm text-gray-900 dark:text-white">
                    {route.url}
                  </div>
                </div>
              </div>

              {/* Quota Info */}
              <div className="mb-6 flex items-center justify-between rounded-lg border border-blue-200/30 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-3 dark:border-blue-700/30 dark:from-blue-900/20 dark:to-indigo-900/20">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Rate Limit
                </div>
                <div className="text-sm font-bold text-blue-700 dark:text-blue-400">
                  {route.quota_limit} {route.quota_unit}
                </div>
              </div>

              {/* Action Buttons */}
              {canManage ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(route);
                    }}
                    className="group/btn flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
                  >
                    <Edit className="h-4 w-4 transition-transform group-hover/btn:scale-110" />
                    Edit YAML
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDelete(route, e);
                    }}
                    className="rounded-lg border-2 border-red-200 bg-red-50 p-2.5 text-red-600 transition-all duration-200 hover:scale-110 hover:bg-red-100 hover:text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                    title="Delete route"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="py-2 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Super Admin access required
                  </p>
                </div>
              )}
            </div>

            {/* Shimmer Effect */}
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full" />
          </div>
        ))}
      </div>

      {filteredRoutes.length === 0 && (
        <Card>
          <CardBody>
            <div className="py-12 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-teal-100 to-cyan-200 dark:from-teal-700 dark:to-cyan-600">
                <Route className="h-12 w-12 text-teal-600 dark:text-teal-300" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                No routes found
              </h3>
              <p className="mx-auto max-w-sm text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? "Try adjusting your search term to find what you're looking for."
                  : "Service routes define how traffic is routed to your backend services. Create your first route to get started."}
              </p>
              {!searchTerm ? (
                <button
                  onClick={handleCreate}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:from-teal-600 hover:to-cyan-700"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Route
                </button>
              ) : (
                <button
                  onClick={() => setSearchTerm("")}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Clear search
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* YAML Editor Modal */}
      {showYamlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 backdrop-blur-sm">
          <Card
            shadow="xl"
            className="flex max-h-[90vh] w-full max-w-5xl flex-col border-0 shadow-2xl"
          >
            <div className="relative">
              {/* Gradient header */}
              <div className="h-20 rounded-t-xl bg-gradient-to-r from-teal-500 to-cyan-600">
                <div className="absolute -bottom-6 left-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-teal-600 to-cyan-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                    <Route className="h-6 w-6" />
                  </div>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowYamlModal(false)}
                className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <CardBody className="flex min-h-0 flex-col pt-8">
              <div className="mb-8 flex-shrink-0">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingRoute
                    ? `Edit Route: ${editingRoute.service_name}`
                    : "Create New Route"}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Configure service routing with YAML syntax for advanced
                  control
                </p>
              </div>

              <div
                className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-2 focus:outline-none"
                tabIndex={0}
              >
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <label className="text-sm font-semibold text-gray-900 dark:text-white">
                      Route Configuration (YAML)
                    </label>
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                      YAML Format
                    </span>
                  </div>
                  <div className="overflow-hidden rounded-xl border-2 border-gray-200 shadow-inner dark:border-gray-700">
                    <Editor
                      height="450px"
                      defaultLanguage="yaml"
                      value={yamlContent}
                      onChange={(value) => setYamlContent(value || "")}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: "on",
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        wordWrap: "on",
                        padding: { top: 16, bottom: 16 },
                        folding: true,
                        bracketPairColorization: { enabled: true },
                      }}
                    />
                  </div>
                  <div className="mt-3 rounded-xl border border-blue-200/30 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:border-blue-700/30 dark:from-blue-900/20 dark:to-indigo-900/20">
                    <div className="flex items-start space-x-3">
                      <Globe className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                      <div>
                        <h4 className="mb-1 text-sm font-semibold text-blue-900 dark:text-blue-200">
                          Configuration Guide
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Edit the YAML configuration above. The format matches
                          the routes.yaml structure with service definitions,
                          endpoints, quotas, and path transformations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowYamlModal(false)}
                    className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveYaml}
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-teal-600 hover:to-cyan-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving && (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    )}
                    {saving
                      ? "Saving..."
                      : editingRoute
                        ? "Update Route"
                        : "Create Route"}
                  </button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
